import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, History, Route, Flag, Clock, CalendarDays, Cpu, User, LineChart } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { useAuth } from "@/context/AuthContext";
import { clampFrom, limitDays, limitText } from "@/lib/reportLimit";
import { fr } from "date-fns/locale";
import { CAN_FIELDS } from "@/lib/can";
import TripChartsModal from "@/components/TripChartsModal";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`);
const fmtHm = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { timeZone: "Pacific/Noumea", hour: "2-digit", minute: "2-digit" });
const fmtDay = (iso) => new Date(iso).toLocaleDateString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit" });

export default function HistoryModal({ vehicle, onClose, onSelectTrip }) {
  const { user } = useAuth();
  const lim = limitDays(user);
  const today = new Date();
  const [range, setRange] = useState({ from: today, to: today });
  const [data, setData] = useState(null);
  const [tripAddr, setTripAddr] = useState({});
  const [can, setCan] = useState([]);
  const [chartTrip, setChartTrip] = useState(null);

  const from = range?.from || today;
  const to = range?.to || range?.from || today;
  const multiDay = ymd(from) !== ymd(to);

  useEffect(() => {
    let cancel = false;
    setData(null);
    setTripAddr({});
    const q = multiDay ? `date_from=${ymd(from)}&date_to=${ymd(to)}` : `date=${ymd(from)}`;
    api.get(`/vehicles/${vehicle.id}/trips?${q}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ count: 0, trips: [], total_km: 0 }); });
    return () => { cancel = true; };
  }, [vehicle.id, ymd(from), ymd(to)]);

  useEffect(() => {
    const spanDays = Math.max(1, Math.round((to - from) / 86400000) + 1);
    api.get(`/vehicles/${vehicle.id}/can-report?hours=${spanDays * 24}`)
      .then((r) => setCan(r.data.fields || [])).catch(() => setCan([]));
  }, [vehicle.id, ymd(from), ymd(to)]);

  useEffect(() => {
    if (!data?.trips?.length) return;
    let cancel = false;
    (async () => {
      for (let i = 0; i < data.trips.length; i++) {
        const t = data.trips[i];
        if (!t.points?.length) continue;
        const s = t.points[0], e = t.points[t.points.length - 1];
        try {
          const rs = await api.get(`/geocode/reverse?lat=${s.lat}&lon=${s.lon}`);
          const re = await api.get(`/geocode/reverse?lat=${e.lat}&lon=${e.lon}`);
          if (!cancel) setTripAddr((a) => ({ ...a, [i]: { start: rs.data.address, end: re.data.address } }));
        } catch { /* ignore */ }
      }
    })();
    return () => { cancel = true; };
  }, [data]);

  const rangeLabel = multiDay
    ? `Du ${from.toLocaleDateString("fr-FR", { day: "2-digit", month: "long" })} au ${to.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}`
    : from.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 animate-slidein" onClick={(e) => e.stopPropagation()} data-testid="history-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="label-mono flex items-center gap-1"><History className="w-3.5 h-3.5" /> Historique des trajets — {vehicle.name}</p>
            <p className="font-bold text-lg">{vehicle.plate}</p>
          </div>
          <button onClick={onClose} data-testid="history-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5">
          <div className="shrink-0">
            <p className="label-mono flex items-center gap-1 mb-1.5"><CalendarDays className="w-3.5 h-3.5" /> Choisir un jour ou une plage</p>
            <div className="rounded-md border border-border bg-secondary/30 p-1" data-testid="history-calendar">
              <Calendar mode="range" locale={fr} selected={range}
                onSelect={(r) => setRange(r && r.from ? r : { from: today, to: today })}
                disabled={[{ after: new Date() }, ...(range?.from && !range?.to ? [clampFrom(user, range.from)] : [])]} />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 px-1">Cliquez un jour, ou un 2ᵉ jour pour cumuler la période.{lim ? ` ${limitText(user)}.` : ""}</p>
          </div>

          <div className="min-w-0">
            <div className="flex items-center justify-between mb-2">
              <p className="label-mono flex items-center gap-1"><Route className="w-3.5 h-3.5 brand-teal" /> Trajets</p>
              <span className="data text-[11px] text-muted-foreground" data-testid="history-trips-total">{data ? `${data.count} · ${data.total_km} km` : "…"}</span>
            </div>
            <p className="text-[11px] text-muted-foreground capitalize mb-2" data-testid="history-range-label">{rangeLabel}</p>

            {can.length > 0 && (
              <div className="mb-3 border border-border rounded-md bg-secondary/30 p-2" data-testid="history-can">
                <p className="label-mono flex items-center gap-1 mb-1.5"><Cpu className="w-3.5 h-3.5 brand-teal" /> Données CAN (période)</p>
                <div className="space-y-1">
                  {can.map((f) => {
                    const Ic = (CAN_FIELDS[f.key] || {}).Icon;
                    return (
                      <div key={f.key} className="flex items-center gap-2 text-[11px]" data-testid={`history-can-${f.key}`}>
                        {Ic && <Ic className="w-3.5 h-3.5 brand-teal shrink-0" />}
                        <span className="text-muted-foreground w-28 shrink-0">{f.label}</span>
                        <span className="data">min {f.min} · moy {f.avg} · max {f.max}{f.unit ? ` ${f.unit}` : ""}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="max-h-[52vh] overflow-y-auto space-y-1.5 pr-0.5" data-testid="history-trips">
              {!data && <p className="text-xs text-muted-foreground py-4 text-center">Chargement des trajets…</p>}
              {data && data.trips.length === 0 && <p className="text-xs text-muted-foreground py-6 text-center">Aucun trajet sur la période.</p>}
              {data && data.trips.map((t, i) => (
                <div key={i}>
                  {!multiDay && t.gap_before_min != null && t.gap_before_min > 0 && (
                    <p className="flex items-center gap-1 text-[10px] text-muted-foreground my-1 pl-1" data-testid={`history-trip-gap-${i}`}>
                      <Clock className="w-3 h-3" /> Arrêt de {fmtDur(t.gap_before_min)} avant ce trajet
                    </p>
                  )}
                  <button type="button" onClick={() => onSelectTrip && onSelectTrip(t)} data-testid={`history-trip-${i}`}
                    className="w-full text-left border border-border rounded-md p-2 bg-secondary/40 hover:border-[#17A2B8] hover:bg-[#17A2B8]/10 transition-colors">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="flex items-center gap-1.5 font-bold text-xs">
                        <Route className="w-3.5 h-3.5 brand-teal" /> Trajet {i + 1}
                        {multiDay && <span className="data text-[10px] text-muted-foreground font-normal">· {fmtDay(t.start)}</span>}
                      </span>
                      <span className="data text-xs font-bold brand-orange">{t.distance_km} km · {fmtDur(t.duration_min)}</span>
                    </div>
                    {t.driver_name && (
                      <div className="flex items-center gap-1 mb-1.5 text-[11px] font-semibold text-[#17A2B8]" data-testid={`history-trip-driver-${i}`}>
                        <User className="w-3 h-3" /> {t.driver_name}
                      </div>
                    )}
                    <div className="space-y-1 data text-[11px]">
                      <div className="flex items-center gap-1.5">
                        <Flag className="w-3.5 h-3.5 text-[#10B981] shrink-0" title="Départ" />
                        <span className="font-semibold shrink-0">{fmtHm(t.start)}</span>
                        <span className="text-muted-foreground truncate">· {tripAddr[i]?.start || "localisation…"}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Flag className="w-3.5 h-3.5 text-[#EF4444] shrink-0" title="Arrivée" />
                        <span className="font-semibold shrink-0">{fmtHm(t.end)}</span>
                        <span className="text-muted-foreground truncate">· {tripAddr[i]?.end || "localisation…"}</span>
                      </div>
                    </div>
                    <span role="button" tabIndex={0} data-testid={`history-modal-trip-charts-${i}`}
                      onClick={(e) => { e.stopPropagation(); setChartTrip(t); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setChartTrip(t); } }}
                      className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-[#2ec5db] hover:underline cursor-pointer">
                      <LineChart className="w-3 h-3" /> Graphiques CAN
                    </span>
                  </button>
                </div>
              ))}
            </div>
            {data && data.trips.length > 0 && <p className="text-[10px] text-muted-foreground mt-2">Cliquez un trajet pour l'afficher sur la carte · heure de Nouméa</p>}
          </div>
        </div>
      </div>
      {chartTrip && <TripChartsModal trip={chartTrip} title={`${vehicle.name} · ${fmtDay(chartTrip.start)} ${fmtHm(chartTrip.start)}`} onClose={() => setChartTrip(null)} />}
    </div>
  );
}
